import React, { useCallback, useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import { useNavigate, useLocation } from "react-router-dom";
import { PlusIcon } from "../../../../Icons";
import {
  deleteDailyLog,
  listDailyLogs,
} from "../../../../services/dailyLogService";
import { printGeneralForms } from "../../../Application/GeneralForms/print/generalFormsPrintService";
import ClinicalFormList from "./ClinicalFormList";
import CollapsibleSection from "../CollapsibleSection";
import useConfirm from "../../../../hooks/useConfirm";
import {
  EVAL_DAILY_LOG_SECTION_KEY,
  dailyLogChartNavState,
  mapDailyLogToEvalForm,
} from "./dailyLogEval";
import { sortClinicalFormsByScheduleDateAsc } from "./sortClinicalForms";

const PatientEvalDailyLogsSection = ({
  patientId,
  title,
  addLabel,
  icon,
  tone,
  canEdit,
  canDelete = false,
  canManageFlags = false,
  patient,
  selectedIds,
  onToggleSelect,
  onToggleSelectAll,
  onFormsLoaded,
  onRemoveMark,
  showSelection = true,
  showMarks = true,
  showCountBadge = false,
  forceOpen = false,
  highlight = false,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const userOverrodeOpenRef = useRef(false);
  const { confirm, confirmDialog } = useConfirm();

  useEffect(() => {
    userOverrodeOpenRef.current = false;
    setOpen(false);
  }, [patientId]);

  useEffect(() => {
    if (forceOpen) {
      setOpen(true);
      userOverrodeOpenRef.current = false;
    }
  }, [forceOpen]);

  const handleOpenChange = useCallback((next) => {
    userOverrodeOpenRef.current = true;
    setOpen(next);
  }, []);

  const syncOpenFromForms = useCallback(
    (nextForms) => {
      const hasForms = (nextForms || []).length > 0;
      if (forceOpen) {
        setOpen(true);
        return;
      }
      if (!hasForms) {
        setOpen(false);
        return;
      }
      if (!userOverrodeOpenRef.current) {
        setOpen(true);
      }
    },
    [forceOpen],
  );

  const loadForms = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listDailyLogs({
        patientId,
        page: 1,
        limit: 200,
      });
      const rows = res.result?.dailyLogs || res.dailyLogs || [];
      const next = sortClinicalFormsByScheduleDateAsc(
        rows.map(mapDailyLogToEvalForm),
      );
      setForms(next);
      syncOpenFromForms(next);
      onFormsLoaded?.(EVAL_DAILY_LOG_SECTION_KEY, next);
    } catch (err) {
      toast.error(err.message || "Failed to load daily logs");
      setForms([]);
      syncOpenFromForms([]);
      onFormsLoaded?.(EVAL_DAILY_LOG_SECTION_KEY, []);
    } finally {
      setLoading(false);
    }
  }, [patientId, onFormsLoaded, syncOpenFromForms]);

  useEffect(() => {
    loadForms();
  }, [loadForms]);

  const navState = dailyLogChartNavState({
    patientId,
    returnTo: `${location.pathname}${location.search || ""}`,
    patient,
  });

  const openNewForm = () => {
    setOpen(true);
    navigate("/general-forms/daily-log", { state: navState });
  };

  const handleOpen = (form) => {
    navigate(`/general-forms/daily-log/${form.dailyLogId}`, { state: navState });
  };

  const handleDelete = async (form) => {
    const ok = await confirm({
      title: "Delete form?",
      message: `"${form.formName}" and everything saved in it will be permanently removed. This cannot be undone.`,
    });
    if (!ok) return;
    try {
      await deleteDailyLog(form.dailyLogId);
      toast.success("Form deleted");
      userOverrodeOpenRef.current = false;
      await loadForms();
    } catch (err) {
      toast.error(err.message || "Failed to delete form");
    }
  };

  const handlePrint = async (form) => {
    await printGeneralForms([{ id: form.dailyLogId }], "daily-log");
  };

  const actions =
    canEdit && forms.length > 0 ? (
      <button
        type="button"
        onClick={openNewForm}
        className={`inline-flex items-center gap-1 h-7 px-2.5 text-xs font-semibold border bg-white rounded-md transition-colors ${tone.addTone}`}
      >
        <PlusIcon size={12} />
        {addLabel}
      </button>
    ) : null;

  return (
    <>
      <CollapsibleSection
        title={title}
        icon={icon}
        tone={tone}
        open={open}
        onOpenChange={handleOpenChange}
        className={
          highlight ? "ring-2 ring-indigo-300 ring-offset-1" : undefined
        }
        badge={
          showCountBadge ? (
            <span className="inline-flex items-center justify-center h-5 min-w-[1.25rem] px-1.5 rounded text-[10px] font-semibold bg-slate-600 text-white tabular-nums">
              {forms.length}
            </span>
          ) : null
        }
        actions={actions}
      >
        <ClinicalFormList
          forms={forms}
          loading={loading}
          canEdit={canEdit}
          canDelete={canDelete}
          canManageFlags={canManageFlags}
          selectedIds={selectedIds}
          onToggleSelect={onToggleSelect}
          onToggleSelectAll={(selectAll) =>
            onToggleSelectAll?.(
              forms.map((f) => f.id),
              selectAll,
            )
          }
          onOpen={handleOpen}
          onDelete={handleDelete}
          onPrint={handlePrint}
          showAudit={false}
          showAttachments
          onAttachmentsChanged={loadForms}
          onAddClick={openNewForm}
          addLabel={addLabel}
          patientId={patientId}
          onRemoveMark={onRemoveMark}
          showSelection={showSelection}
          showMarks={showMarks}
        />
      </CollapsibleSection>
      {confirmDialog}
    </>
  );
};

export default PatientEvalDailyLogsSection;
